/**
 * @file useComicReaderPage.js
 * @description 漫画阅读页 facade composable，组合各子 composable 并负责生命周期与卷切换
 * @module features/comic/composables/useComicReaderPage
 */

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useComicPages } from './useComicPages'
import { useComicScroll } from './useComicScroll'
import { useComicCatalog } from './useComicCatalog'
import { useComicVolumeNav } from './useComicVolumeNav'
import { useComicInteract } from './useComicInteract'
import { useComicDanmaku } from './useComicDanmaku'
import { useReaderVisualViewport } from './useReaderVisualViewport'
import { resolveProgressContextFromState } from '../utils/resolveProgressContext'
import { AUTH_CHANGED_EVENT, VOLS_UNLOCKED_EVENT } from '@/shared/utils/authEvents'
import { getAuthToken } from '@/shared/utils/auth'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { markVolsUnlocked } from '../utils/volAccess'

/**
 * @param {import('vue').Ref<string|number>} comicIdRef
 * @param {import('vue').Ref<string|number>} volNumRef
 * @param {(event: 'toggle-nav-footer', hidden: boolean) => void} emit
 */
export function useComicReaderPage(comicIdRef, volNumRef, emit) {
  useReaderVisualViewport()

  const route = useRoute()
  const router = useRouter()
  const readerContainer = ref(null)
  const catalogContentRef = ref(null)
  const danmakuBarRef = ref(null)

  const state = reactive({
      pages: [], // 当前卷的图片列表
      token: '',
      imageRefs: [], // 图片元素的引用数组
      observer: null, // Intersection Observer 实例
      lastImageObserver: null, // 监听最后一个图片的 Observer（用于自动加载下一卷）
      currentPageIndex: 0, // 当前阅读的图片索引（全局，跨已加载卷）
      maxSavedPageIndex: -1, // 当前卷内已保存的最大 pageIndex
      currentProgressVolNum: null, // 当前进度追踪所对应的卷号
      saveProgressTimer: null, // 防抖定时器
      isRestoringProgress: false, // 是否正在恢复进度
      progressKey: '', // localStorage 的 key
      lastScrollTop: 0, // 上次滚动位置，用于判断滚动方向
      scrollTimer: null, // 滚动防抖定时器
      isMobile: false, // 是否为移动端
      // 顶部弹窗条相关
      showTopBar: false, // 是否显示顶部弹窗条
      showCatalog: false, // 是否显示目录
      totalVolumes: 0, // 总卷数
      volumeList: [], // 卷号数组（旧数据，保留兼容）
      volInfoVos: [], // 卷信息数组（从详情接口获取，包含 id、num、title）
      catalogData: null, // 目录数据（新接口返回的数据）
      isLoadingCatalog: false, // 是否正在加载目录
      // 分页相关状态
      currentPageNum: 1, // 当前页码
      pageSize: 10, // 每页容量
      totalPages: 0, // 总页数
      totalCount: 0, // 总文件数
      hasNext: false, // 是否有下一页
      hasPrev: false, // 是否有上一页
      isLoading: false, // 是否正在加载
      pagesFetchToken: 0, // 卷切换时递增，用于丢弃过期的分页请求
      /** 当前卷未解锁：一屏预览 + 禁止滚动 */
      pagesLocked: false,
      /** 邻卷预览已铺好时，跳过随后一次路由卷切换，避免闪空白 */
      suppressVolumeSwitchOnce: false,
      /** 与路由同步的卷号（避免 reactive 上 getter 不可靠） */
      routeVolNum: null,
      /** 漫画名称（详情接口，供打赏等展示） */
      comicName: '',
      /** 目录/路由跳卷后，禁止自动预加载相邻卷，直到用户主动滚动 */
      allowAutoLoadAdjacentVolume: true,
      loadNextPageThreshold: 8, // 加载下一页的阈值（滑动到第几张图片时开始加载）
      lastLoadTriggerIndex: -1, // 上次触发加载的图片索引，避免重复加载
      // 互动区域相关
      volId: null, // 当前卷ID
      interactData: null, // 互动数据
      isLoadingInteract: false, // 是否正在加载互动数据
      isLoadingMoreComments: false, // 是否正在加载更多评论
      currentUser: null, // 当前登录用户信息
      commentInput: '', // 评论输入内容
      // 卷评论回复相关
      replyContentMap: {},
      showReplyBoxMap: {},
      submittingReplyMap: {},
      loadingRepliesMap: {},
      pendingExpandRepliesMap: {},
      showAllRepliesMap: {},
      replyPageSize: 5,
      // 下一卷相关
      nextVolumePages: [], // 下一卷的图片列表（包含所有后续已加载卷的图片）
      isLoadingNextVolume: false, // 是否正在加载下一卷
      nextVolumeNum: null, // 下一卷的卷号（当前显示的下一卷）
      nextVolumeVolId: null, // 下一卷的ID
      nextVolumeCurrentPage: 1, // 下一卷的当前页码
      nextVolumeHasNext: false, // 下一卷是否有下一页
      nextVolumeLastLoadTriggerIndex: -1, // 下一卷上次触发加载的图片索引
      nextVolumeInteractData: null, // 下一卷的互动数据
      isLoadingNextVolumeInteract: false, // 是否正在加载下一卷的互动数据
      nextVolumeCommentInput: '', // 下一卷的评论输入内容
      loadedVolumes: [], // 已完成的后续卷信息数组，每个元素包含 { volNum, volId, startIndex, endIndex, interactData, commentInput, images }
      isCheckingNextVolume: false, // 是否正在检查并加载下一卷（防止重复调用）
      isCurrentVolumeComplete: false, // 当前卷是否完成（图片加载完成 + 互动区加载完成）
      showLoadNextVolumePrompt: false, // 是否显示加载下一卷的提示
      scrollTriggeredLoadNext: false, // 是否已经通过滚动触发了加载下一卷（防止重复触发）
      // 上一卷相关
      prevVolumePages: [], // 上一卷的图片列表
      isLoadingPrevVolume: false, // 是否正在加载上一卷
      prevVolumeNum: null, // 上一卷的卷号
      prevVolumeVolId: null, // 上一卷的ID
      prevVolumeCurrentPage: 1, // 上一卷的当前页码
      prevVolumeHasNext: false, // 上一卷是否有下一页
      prevVolumeInteractData: null, // 上一卷的互动数据
      prevVolumeCommentInput: '', // 上一卷的评论输入内容
      loadedPrevVolumes: [], // 已加载的上一卷信息数组，每个元素包含 { volNum, volId, startIndex, endIndex, interactData, commentInput, images }
      showLoadPrevVolumePrompt: false, // 是否显示加载上一卷的提示
      scrollTriggeredLoadPrev: false, // 是否已经通过滚动触发了加载上一卷（防止重复触发）
      loadVolumePromptText: '', // 加载提示文本（"正在加载下一卷..." 或 "正在加载上一卷..."）
      currentDisplayVolNum: null, // 当前正在显示的卷号（动态更新）
      // --- 弹幕（列表接口待定，当前为本地假数据） ---
      danmakuList: [],
      danmakuEnabled: true,
      danmakuTextOpacity: 0.92,
      danmakuDurationMin: 12,
      danmakuDurationMax: 45,
      danmakuFontSizeEnum: 2,
      danmakuSpeedEnum: 3,
      /** 弹幕显示区域高度占视口高度的百分比（与后端 displayArea 对齐） */
      danmakuDisplayAreaPercent: 18,
      showDanmakuSendDialog: false,
      showDanmakuSettingsDialog: false,
      danmakuDraft: '',
      danmakuHighlightClearTimer: null,
      danmakuFlyActive: false,
      danmakuFlyPlaying: false,
      danmakuFlyText: '',
      danmakuFlyVars: {},
      danmakuPendingFly: null,
      danmakuLaunchLoading: false,
      danmakuLaunchLoaded: false,
      danmakuInteractOpen: false,
      danmakuInteractDanmakuId: '',
      danmakuInteractContent: '',
      danmakuInteractLikeCount: 0,
      danmakuInteractIsLiked: false,
      danmakuInteractLikedLoading: false,
      danmakuInteractAutoCloseTimer: null,
      _danmakuSettingsSnapshot: '',
      /** 右下角弹幕开关 / 设置 / 发射 FAB，默认收起，点此阅读空白处切换显示 */
      danmakuFabVisible: false
  })
  const ctx = state

  Object.assign(ctx, {
    get comicId() { return comicIdRef.value },
    get volNum() { return ctx.routeVolNum ?? route.params.volNum ?? volNumRef.value },
    get $route() { return route },
    get $router() { return router },
    get $refs() { return { readerContainer: readerContainer.value, danmakuBarRef: danmakuBarRef.value, catalogContent: catalogContentRef.value } },
    catalogContentRef,
    get $el() { return readerContainer.value },
    $nextTick: nextTick,
    $emit: emit,
  })

  function getActiveVolNum() {
    return Number(route.params.volNum ?? ctx.routeVolNum ?? volNumRef.value)
  }

  function updateIsMobile() {
    const isMobileWidth = isMobileLayoutViewport()
    const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    ctx.isMobile = isMobileWidth || isMobileUserAgent
  }

  function goToHome() {
    ctx.$router.push({ name: 'Home' })
  }

  const bridge = {}
  useComicCatalog(bridge, ctx)
  useComicInteract(bridge, ctx)
  const danmakuComputed = useComicDanmaku(bridge, ctx)
  useComicPages(bridge, ctx, readerContainer)
  useComicScroll(bridge, ctx, readerContainer)
  useComicVolumeNav(bridge, ctx)
  Object.assign(bridge, {
    updateIsMobile,
    goToHome,
  })
  const {
    setImageRef,
    setLoadedVolumeImageRef,
    fetchComicPages,
    loadNextPage,
    initProgressTracking,
    setupLastImageObserver,
    updateCurrentPage,
    debouncedSaveProgress,
    saveProgress,
    syncProgressToServer,
    restoreProgress,
    waitForImagesLoaded,
    resetScrollPosition,
    handleVisibilityChange,
    handleBeforeUnload,
    detectCurrentDisplayVolume,
    handleScroll,
    fetchTotalVolumes,
    getVolIdFromVolInfoVos,
    redirectToTargetVolIfNeeded,
    toggleCatalog,
    fetchCatalog,
    closeCatalog,
    goToVolume,
    getVolNumFromTitle,
    formatDate,
    getProgressOffset,
    getCatalogActiveVolNum,
    checkAndLoadNextVolume,
    prevVolume,
    nextVolume,
    loadNextVolumeAfterScroll,
    loadNextVolumeAllPages,
    fetchLoadedVolumeInteractData,
    loadPrevVolumeAfterScroll,
    loadPrevVolumeAllPages,
    loadNextVolume,
    loadNextVolumeNextPage,
    getNextVolumeVolIdAndFetchInteract,
    fetchNextVolumeInteractData,
    loadNextVolumeAfterCurrent,
    setNextVolumeImageRef,
    getCurrentVolumeImages,
    getCurrentVolumeStartIndex,
    shouldShowNextVolumeLoading,
    getLoadingNextVolumeLabel,
    readerCommentElId,
    tryScrollToHighlightedVolComment,
    fetchInteractData,
    checkCurrentVolumeComplete,
    fetchCurrentUser,
    handleLike,
    handleCollect,
    handleReward,
    submitComment,
    handleCommentLike,
    handleLoadedVolumeCommentLike,
    loadMoreComments,
    collapseComments,
    getAvatarUrl,
    getCommentAvatarUrl,
    handleAvatarError,
    handleCommentAvatarError,
    formatCommentTime,
    handleLoadedVolumeLike,
    handleLoadedVolumeCollect,
    submitLoadedVolumeComment,
    toggleReplyBox,
    toggleShowAllReplies,
    toggleVolCommentReplies,
    submitVolCommentReply,
    volCommentHasReplies,
    deleteVolComment,
    deleteVolCommentReply,
    bootstrapDanmakuUserConf,
    fetchDanmakuUserConfFromServer,
    applyDanmakuConfFromApi,
    danmakuFontEnumToPx,
    migratePxToFontEnum,
    danmakuSpeedEnumToDurationSec,
    migrateDurationToSpeedEnum,
    danmakuFontSizeEnumLabel,
    danmakuSpeedEnumLabel,
    danmakuFontSizeSliderTooltip,
    danmakuSpeedSliderTooltip,
    danmakuDisplayAreaSliderTooltip,
    buildDanmakuConfUpdatePayload,
    saveDanmakuUserConfRemote,
    toggleDanmakuVisibilityFab,
    onReaderChromeTapToggleDanmakuFab,
    onDanmakuSettingsOpened,
    onDanmakuSettingsClosed,
    loadDanmakuSettings,
    saveDanmakuSettings,
    fetchDanmakuLaunchQuery,
    extractDanmakuLaunchPageContents,
    normalizeDanmakuRowIdFromPayload,
    applyDanmakuLaunchContents,
    clearDanmakuInteractAutoCloseTimer,
    scheduleDanmakuInteractAutoClose,
    closeDanmakuInteract,
    onDanmakuSegmentClick,
    toggleDanmakuInteractLike,
    openDanmakuSendDialog,
    danmakuSegmentsForTrack,
    danmakuLineScrollStyle,
    danmakuItemInlineStyle,
    submitDanmakuSend,
    onDanmakuFlyAnimationEnd,
    danmakuAfterFlySendAndRefresh,
  } = bridge

  const readingProgress = computed(() => {
      if (!ctx.pages || ctx.pages.length === 0) {
        return 0
      }
      // 计算已阅读的进度：当前图片索引 + 1 / 总图片数 * 100
      return Math.min(((ctx.currentPageIndex + 1) / ctx.pages.length) * 100, 100)
  })

  let lastVolumeSwitchAt = 0
  let lastVolumeSwitchVol = null

  function applyVolumeSwitch(targetVolNum, reason) {
    const nextVol = Number(targetVolNum)
    const now = Date.now()
    if (lastVolumeSwitchVol === nextVol && now - lastVolumeSwitchAt < 400) {
            return
    }
    lastVolumeSwitchVol = nextVol
    lastVolumeSwitchAt = now

    ctx.pagesFetchToken += 1
    ctx.isLoading = false
    ctx.isRestoringProgress = false
    ctx.routeVolNum = String(nextVol)
    ctx.allowAutoLoadAdjacentVolume = false
    resetScrollPosition()
    ctx.progressKey = `reading_progress_${ctx.comicId}_${nextVol}`
    ctx.pages = []
    ctx.imageRefs = []
    ctx.currentPageIndex = 0
    ctx.maxSavedPageIndex = -1
    ctx.currentProgressVolNum = nextVol
    ctx.currentPageNum = 1
    ctx.totalPages = 0
    ctx.totalCount = 0
    ctx.hasNext = false
    ctx.hasPrev = false
    ctx.pagesLocked = false
    ctx.lastLoadTriggerIndex = -1
    ctx.showTopBar = false
    ctx.showCatalog = false
    ctx.catalogData = null
    ctx.volId = null
    ctx.interactData = null
    ctx.isLoadingInteract = false
    ctx.nextVolumePages = []
    ctx.isLoadingNextVolume = false
    ctx.nextVolumeNum = null
    ctx.nextVolumeVolId = null
    ctx.nextVolumeCurrentPage = 1
    ctx.nextVolumeHasNext = false
    ctx.nextVolumeLastLoadTriggerIndex = -1
    ctx.nextVolumeInteractData = null
    ctx.isLoadingNextVolumeInteract = false
    ctx.nextVolumeCommentInput = ''
    ctx.loadedVolumes = []
    ctx.isCheckingNextVolume = false
    ctx.isCurrentVolumeComplete = false
    ctx.showLoadNextVolumePrompt = false
    ctx.scrollTriggeredLoadNext = false
    ctx.prevVolumePages = []
    ctx.isLoadingPrevVolume = false
    ctx.prevVolumeNum = null
    ctx.prevVolumeVolId = null
    ctx.prevVolumeCurrentPage = 1
    ctx.prevVolumeHasNext = false
    ctx.prevVolumeInteractData = null
    ctx.prevVolumeCommentInput = ''
    ctx.loadedPrevVolumes = []
    ctx.showLoadPrevVolumePrompt = false
    ctx.scrollTriggeredLoadPrev = false
    ctx.loadVolumePromptText = ''
    closeDanmakuInteract()
    ctx.danmakuList = []
    ctx.danmakuLaunchLoaded = false
    ctx.danmakuLaunchLoading = false
    ctx.currentDisplayVolNum = nextVol

    if (ctx.observer) {
      ctx.observer.disconnect()
      ctx.observer = null
    }
    if (ctx.lastImageObserver) {
      ctx.lastImageObserver.disconnect()
      ctx.lastImageObserver = null
    }

    fetchCurrentUser()
    fetchTotalVolumes().then(() => {
            fetchComicPages(1, false, nextVol)
    })
  }

  bridge.applyVolumeSwitch = applyVolumeSwitch
  bridge.getActiveVolNum = getActiveVolNum

  watch(
    () => route.params.volNum,
    (newVolNum) => {
      if (newVolNum != null && String(newVolNum).trim() !== '') {
        ctx.routeVolNum = String(newVolNum)
      }
    },
    { immediate: true }
  )

  watch(
    () => route.params.volNum,
    (newVolNum, oldVolNum) => {
        if (oldVolNum === undefined) {
          return
        }
        if (ctx.suppressVolumeSwitchOnce) {
          ctx.suppressVolumeSwitchOnce = false
          ctx.routeVolNum = String(newVolNum)
          return
        }
        applyVolumeSwitch(newVolNum, 'route-watch')
    }
  )

  watch(() => ctx.pages, () => {
        if (ctx.pages.length > 0) {
          ctx.$nextTick(() => {
            if (ctx.pagesLocked) {
              ctx.currentDisplayVolNum = Number(ctx.routeVolNum ?? ctx.volNum)
              return
            }
            // 只有在不是恢复进度时才初始化
            if (!ctx.isRestoringProgress) {
              initProgressTracking()
              // 检查当前卷是否完成
              checkCurrentVolumeComplete()
            }
            // 检测并更新当前显示的卷号
            setTimeout(() => {
              if (ctx.pagesLocked) return
              const detectedVolNum = detectCurrentDisplayVolume()
              if (detectedVolNum !== ctx.currentDisplayVolNum) {
                ctx.currentDisplayVolNum = detectedVolNum
              }
            }, 100) // 延迟100ms确保DOM渲染完成
          })
        }
  }, { deep: true })

  watch(() => ctx.interactData, () => {
    checkCurrentVolumeComplete()
  }, { deep: true })

  watch(() => ctx.hasNext, () => {
    checkCurrentVolumeComplete()
  })

  watch(() => ctx.danmakuEnabled, () => { saveDanmakuSettings() })

  watch(() => ctx.danmakuTextOpacity, () => { saveDanmakuSettings() })

  watch(() => ctx.danmakuSpeedEnum, () => { saveDanmakuSettings() })

  watch(() => ctx.danmakuFontSizeEnum, () => { saveDanmakuSettings() })

  watch(() => ctx.danmakuDisplayAreaPercent, () => { saveDanmakuSettings() })

  watch(() => ctx.showDanmakuSendDialog, (val) => {
    if (val) {
      ctx.danmakuFabVisible = false
      ctx.showTopBar = false
    }
  })

  watch(() => ctx.showDanmakuSettingsDialog, (val) => {
    if (val) {
      ctx.danmakuFabVisible = false
      ctx.showTopBar = false
    }
  })

  watch(() => ctx.volId, (newVal) => {
    if (newVal && ctx.comicId) {
      fetchDanmakuLaunchQuery().catch((err) => {
        console.error('弹幕列表加载失败:', err)
      })
    }
  })

  function onAuthChanged() {
    ctx.token = getAuthToken()
    if ((ctx.pages.length === 0 || ctx.pagesLocked) && ctx.comicId) {
      void fetchComicPages(1, false)
    }
    void fetchTotalVolumes()
  }

  function onVolsUnlocked(event) {
    const comicId = event?.detail?.comicId
    if (comicId && String(comicId) !== String(ctx.comicId)) return
    markVolsUnlocked(ctx.volInfoVos, event?.detail?.unlockedVolIds || [])
    if (ctx.catalogData?.volArray) {
      markVolsUnlocked(ctx.catalogData.volArray, event?.detail?.unlockedVolIds || [])
    }
  }

  onMounted(() => {
    // 检测是否为移动端
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)

    // 进入观看漫画页面时，隐藏导航栏和底部区域
    ctx.$emit('toggle-nav-footer', true)

    // 先拉卷信息（含 volId↔volNum），再按 targetVolId 纠正路由，最后加载画页
    fetchTotalVolumes()
      .then(async () => {
        const redirected = await redirectToTargetVolIfNeeded()
        if (!redirected) {
          fetchComicPages()
        }
      })
      .catch(() => {
        fetchComicPages()
      })

    // 获取当前用户信息 + 弹幕默认配置（登录后拉服务端）
    fetchCurrentUser().finally(() => {
      bootstrapDanmakuUserConf()
    })

    // 初始化当前显示的卷号为当前卷号
    ctx.currentDisplayVolNum = Number(ctx.volNum)
    // 监听页面可见性变化，离开时保存进度
    document.addEventListener('visibilitychange', handleVisibilityChange)
    // 监听页面卸载，离开时保存进度
    window.addEventListener('beforeunload', handleBeforeUnload)

    // 初始化滚动位置
    ctx.lastScrollTop = 0
    if (ctx.$refs.readerContainer) {
      ctx.lastScrollTop = ctx.$refs.readerContainer.scrollTop || 0
    }

    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
    window.addEventListener(VOLS_UNLOCKED_EVENT, onVolsUnlocked)

    if (process.env.NODE_ENV === 'local' || process.env.NODE_ENV === 'development') {
      window.__comicReaderTest = {
        updateCurrentPage,
        saveProgress,
        resolveProgressContext: (globalPageIndex) =>
          resolveProgressContextFromState(
            {
              comicId: ctx.comicId,
              volNum: ctx.volNum,
              routeVolNum: getActiveVolNum(),
              totalCount: ctx.totalCount,
              pages: ctx.pages,
              loadedVolumes: ctx.loadedVolumes,
              nextVolumePages: ctx.nextVolumePages,
              nextVolumeNum: ctx.nextVolumeNum,
            },
            globalPageIndex,
            getActiveVolNum(),
          ),
        getProgressSnapshot() {
          return {
            comicId: ctx.comicId,
            volNum: ctx.volNum,
            currentPageIndex: ctx.currentPageIndex,
            maxSavedPageIndex: ctx.maxSavedPageIndex,
            currentProgressVolNum: ctx.currentProgressVolNum,
            pagesLen: ctx.pages.length,
            nextVolumeNum: ctx.nextVolumeNum,
            nextVolumePagesLen: ctx.nextVolumePages.length,
            loadedVolumes: ctx.loadedVolumes.map((v) => ({
              volNum: v.volNum,
              startIndex: v.startIndex,
              endIndex: v.endIndex,
              imageCount: v.images?.length ?? 0,
            })),
          }
        },
        getLocalProgress() {
          return JSON.parse(localStorage.getItem('reading_progress') || '{}')
        },
        /** 模拟连续阅读进入下一卷（用于 E2E 验证进度写入逻辑） */
        simulateContinuousReadVol2(localPageIndex = 5) {
          const nextVol = Number(getActiveVolNum()) + 1
          ctx.nextVolumeNum = nextVol
          const imageCount = Math.max(localPageIndex + 5, 10)
          ctx.nextVolumePages = Array.from({ length: imageCount }, (_, i) => `sim://page-${i}`)
          const globalIndex = ctx.pages.length + localPageIndex
          updateCurrentPage(globalIndex)
          saveProgress(true)
          return resolveProgressContextFromState(
            {
              comicId: ctx.comicId,
              volNum: ctx.volNum,
              routeVolNum: getActiveVolNum(),
              totalCount: ctx.totalCount,
              pages: ctx.pages,
              loadedVolumes: ctx.loadedVolumes,
              nextVolumePages: ctx.nextVolumePages,
              nextVolumeNum: ctx.nextVolumeNum,
            },
            globalIndex,
            getActiveVolNum(),
          )
        },
      }
    }
  })

  onBeforeUnmount(() => {
    // 清理 Intersection Observer
    if (ctx.observer) {
      ctx.observer.disconnect()
      ctx.observer = null
    }
    // 清理定时器
    if (ctx.saveProgressTimer) {
      clearTimeout(ctx.saveProgressTimer)
    }
    if (ctx.scrollTimer) {
      clearTimeout(ctx.scrollTimer)
    }
    if (ctx.danmakuHighlightClearTimer) {
      clearTimeout(ctx.danmakuHighlightClearTimer)
      ctx.danmakuHighlightClearTimer = null
    }
    ctx.danmakuFlyActive = false
    ctx.danmakuFlyPlaying = false
    closeDanmakuInteract()
    // 移除事件监听
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)
    window.removeEventListener('resize', updateIsMobile)
    window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
    window.removeEventListener(VOLS_UNLOCKED_EVENT, onVolsUnlocked)
    // 滚动事件通过模板 @scroll 绑定，Vue 会自动清理，无需手动移除
    // 离开阅读页面时恢复导航条显示
    ctx.$emit('toggle-nav-footer', false)
    // 最后保存一次进度
    saveProgress(true)
  })

  // created
  ctx.token = JSON.parse(localStorage.getItem('user'))?.token || ''
  ctx.routeVolNum = route.params.volNum != null ? String(route.params.volNum) : String(volNumRef.value)
  ctx.currentProgressVolNum = getActiveVolNum()
  // 即使未登录也允许阅读（但不会保存进度到后端）
  ctx.progressKey = `reading_progress_${ctx.comicId}_${ctx.volNum}`
  loadDanmakuSettings()

  return {
    readerContainer,
    catalogContentRef,
    danmakuBarRef,
    ...danmakuComputed,
    ...toRefs(state),
    readingProgress,
    updateIsMobile,
    setImageRef,
    setLoadedVolumeImageRef,
    fetchComicPages,
    initProgressTracking,
    setupLastImageObserver,
    checkAndLoadNextVolume,
    updateCurrentPage,
    loadNextPage,
    debouncedSaveProgress,
    saveProgress,
    syncProgressToServer,
    restoreProgress,
    waitForImagesLoaded,
    prevVolume,
    nextVolume,
    resetScrollPosition,
    handleVisibilityChange,
    handleBeforeUnload,
    detectCurrentDisplayVolume,
    handleScroll,
    fetchTotalVolumes,
    getVolIdFromVolInfoVos,
    redirectToTargetVolIfNeeded,
    readerCommentElId,
    tryScrollToHighlightedVolComment,
    fetchInteractData,
    checkCurrentVolumeComplete,
    fetchCurrentUser,
    handleLike,
    handleCollect,
    handleReward,
    submitComment,
    handleCommentLike,
    handleLoadedVolumeCommentLike,
    loadMoreComments,
    collapseComments,
    getAvatarUrl,
    getCommentAvatarUrl,
    handleAvatarError,
    handleCommentAvatarError,
    formatCommentTime,
    goToHome,
    toggleCatalog,
    fetchCatalog,
    closeCatalog,
    bootstrapDanmakuUserConf,
    fetchDanmakuUserConfFromServer,
    applyDanmakuConfFromApi,
    danmakuFontEnumToPx,
    migratePxToFontEnum,
    danmakuSpeedEnumToDurationSec,
    migrateDurationToSpeedEnum,
    danmakuFontSizeEnumLabel,
    danmakuSpeedEnumLabel,
    danmakuFontSizeSliderTooltip,
    danmakuSpeedSliderTooltip,
    danmakuDisplayAreaSliderTooltip,
    buildDanmakuConfUpdatePayload,
    saveDanmakuUserConfRemote,
    toggleDanmakuVisibilityFab,
    onReaderChromeTapToggleDanmakuFab,
    onDanmakuSettingsOpened,
    onDanmakuSettingsClosed,
    loadDanmakuSettings,
    saveDanmakuSettings,
    fetchDanmakuLaunchQuery,
    extractDanmakuLaunchPageContents,
    normalizeDanmakuRowIdFromPayload,
    applyDanmakuLaunchContents,
    clearDanmakuInteractAutoCloseTimer,
    scheduleDanmakuInteractAutoClose,
    closeDanmakuInteract,
    onDanmakuSegmentClick,
    toggleDanmakuInteractLike,
    openDanmakuSendDialog,
    danmakuSegmentsForTrack,
    danmakuLineScrollStyle,
    danmakuItemInlineStyle,
    submitDanmakuSend,
    onDanmakuFlyAnimationEnd,
    danmakuAfterFlySendAndRefresh,
    goToVolume,
    getVolNumFromTitle,
    formatDate,
    getProgressOffset,
    getCatalogActiveVolNum,
    loadNextVolumeAfterScroll,
    loadNextVolumeAllPages,
    fetchLoadedVolumeInteractData,
    loadPrevVolumeAfterScroll,
    loadPrevVolumeAllPages,
    loadNextVolume,
    loadNextVolumeNextPage,
    getNextVolumeVolIdAndFetchInteract,
    fetchNextVolumeInteractData,
    loadNextVolumeAfterCurrent,
    setNextVolumeImageRef,
    getCurrentVolumeImages,
    getCurrentVolumeStartIndex,
    shouldShowNextVolumeLoading,
    getLoadingNextVolumeLabel,
    handleLoadedVolumeLike,
    handleLoadedVolumeCollect,
    submitLoadedVolumeComment,
    toggleReplyBox,
    toggleShowAllReplies,
    toggleVolCommentReplies,
    submitVolCommentReply,
    volCommentHasReplies,
    deleteVolComment,
    deleteVolCommentReply,
  }
}
